import React, { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";

/**
 * NumberField — a mobile-safe numeric input.
 *
 * Why this exists:
 * Native `<input type="number">` used as a *controlled* React input that
 * reformats its value on every keystroke (e.g. `.toFixed(2)` /
 * `parseFloat(x)*100`) breaks badly on iOS Safari / WebKit. WebKit returns an
 * empty string from `input.value` for any momentarily-invalid number, and
 * re-imposing a formatted value on each change resets the caret and coerces the
 * text — which made digits get dropped/garbled and Backspace do nothing.
 *
 * This component instead:
 *  - uses `type="text"` + `inputMode` so we rely on normal browser text editing
 *    (backspace, clearing, selection, caret all behave natively),
 *  - keeps the RAW typed string in local state and NEVER reformats while typing,
 *  - only parses + normalizes on blur (and the parent reads the committed value
 *    on Save).
 *
 * Props:
 *  - value: the current numeric value (in display units, e.g. dollars, cm, dpi)
 *  - onCommit(number): called on blur with the normalized number
 *  - allowDecimal: permit a single "." (for currency / size / bleed)
 *  - currency: display with exactly 2 decimals when not focused
 *  - min: minimum allowed value (default 0); empty/invalid falls back to min
 */
export const NumberField = ({
  value,
  onCommit,
  allowDecimal = false,
  currency = false,
  optional = false,
  min = 0,
  className,
  placeholder,
  onLiveChange,
  ...rest
}) => {
  const format = useCallback((v) => {
    if (v === null || v === undefined || v === "" || Number.isNaN(Number(v))) return "";
    return currency ? Number(v).toFixed(2) : String(v);
  }, [currency]);

  const [text, setText] = useState(() => format(value));
  const focusedRef = useRef(false);

  // Reflect external value changes (initial load, Save reload), but never while
  // the user is actively typing so we don't reformat mid-entry.
  useEffect(() => {
    if (!focusedRef.current) setText(format(value));
  }, [value, format]);

  const sanitize = (raw) => {
    if (allowDecimal) {
      let v = raw.replace(/[^0-9.]/g, "");
      const parts = v.split(".");
      if (parts.length > 2) v = parts[0] + "." + parts.slice(1).join("");
      return v;
    }
    return raw.replace(/[^0-9]/g, "");
  };

  const handleChange = (e) => {
    const v = sanitize(e.target.value);
    setText(v);
    if (typeof onLiveChange === "function") onLiveChange(v);
  };

  const commit = () => {
    focusedRef.current = false;
    if (optional && text.trim() === "") {
      if (typeof onCommit === "function") onCommit(null);
      setText("");
      return;
    }
    let num = parseFloat(text);
    if (text === "" || text === "." || Number.isNaN(num)) num = min ?? 0;
    if (min !== undefined && num < min) num = min;
    if (typeof onCommit === "function") onCommit(num);
    setText(format(num));
  };

  return (
    <Input
      {...rest}
      type="text"
      inputMode={allowDecimal ? "decimal" : "numeric"}
      value={text}
      placeholder={placeholder}
      className={className}
      autoComplete="off"
      onFocus={() => { focusedRef.current = true; }}
      onChange={handleChange}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
    />
  );
};
